import React, { useCallback, useState } from 'react';
import { Menu, MenuItem, Typography } from '@mui/material';

import { RetailerNetworkLocationWithPerformanceExcerptFragment } from '@occc/graphql';
import { TableRow, TableCell, IconButton, Divider, MoreVertIcon } from '@occc/ui-design-system';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import { RemoveLocationsFromRetailerNetworkLocationGroup } from '@occc-app/App/PolicyBasedAccessControl/userActions';

import { isPerformanceOnPace } from '../retailer-connect.utils';

type MenuNavigationItemName = 'removeLocationFromGroup' | 'locationDetails';

type RetailerNetworkLocationGroupLocationsTableRowProps = {
  retailerNetworkUuid: string;
  location: RetailerNetworkLocationWithPerformanceExcerptFragment;
  setRemoveLocationDialogOpen: (locationUuid: string | null) => void;
  handleOpenLocationDetailsPage: (locationUuid: string) => void;
};

export const RetailerNetworkLocationGroupLocationsTableRow = ({
  retailerNetworkUuid,
  location,
  setRemoveLocationDialogOpen,
  handleOpenLocationDetailsPage,
}: RetailerNetworkLocationGroupLocationsTableRowProps) => {
  const [actionsForUuid, setActionsForUuid] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const getHandlerForAdditionalActions =
    (location: RetailerNetworkLocationWithPerformanceExcerptFragment) =>
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      setAnchorEl(event.currentTarget);
      setActionsForUuid(location.locationUuid);
    };

  const handleClose = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    setActionsForUuid(null);
  }, []);

  const onMenuNavigationClick =
    (itemName: MenuNavigationItemName) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      if (itemName === 'locationDetails') {
        handleOpenLocationDetailsPage(location.locationUuid);
      }

      if (itemName === 'removeLocationFromGroup') {
        setRemoveLocationDialogOpen(location.locationUuid);
      }

      handleClose(event);
    };

  return (
    <TableRow
      key={location.locationUuid}
      onClick={() => handleOpenLocationDetailsPage(location.locationUuid)}
    >
      <TableCell>{location.siteId}</TableCell>
      <TableCell>{location.name}</TableCell>
      <TableCell align='right'>
        <Typography
          color={
            isPerformanceOnPace(location.newMembers.progress, location.newMembers.goal)
              ? 'success.main'
              : 'error'
          }
        >
          <b>{location.newMembers.progress}</b>
          {`/${location.newMembers.goal}`}
        </Typography>
      </TableCell>
      <TableCell align='right'>
        <Typography
          color={
            isPerformanceOnPace(location.transactions.progress, location.transactions.goal)
              ? 'success.main'
              : 'error'
          }
        >
          <b>{location.transactions.progress}</b>
          {`/${location.transactions.goal}`}
        </Typography>
      </TableCell>
      <TableCell align='right'>
        <IconButton onClick={getHandlerForAdditionalActions(location)} sx={{ p: 0 }}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          open={location.locationUuid === actionsForUuid}
          onClose={handleClose}
          anchorEl={anchorEl}
          PaperProps={{
            sx: {
              borderRadius: '4px',
            },
          }}
        >
          <MenuItem onClick={onMenuNavigationClick('locationDetails')}>
            View Location Details
          </MenuItem>

          <Can I={RemoveLocationsFromRetailerNetworkLocationGroup({ uuid: retailerNetworkUuid })}>
            <Divider />
            <MenuItem onClick={onMenuNavigationClick('removeLocationFromGroup')}>
              Remove Location From Location Group
            </MenuItem>
          </Can>
        </Menu>
      </TableCell>
    </TableRow>
  );
};
